import {
	type ProductVariantChannelAvailabilityMap,
	type ProductVariant,
	type LineItem,
} from '#node_modules/@commercetools/platform-sdk/dist/commercetools-platform-sdk.cjs'
import { WheelGesturesPlugin } from '#node_modules/embla-carousel-wheel-gestures'
import { Link, useFetcher, useLoaderData, useLocation } from '@remix-run/react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ImageComponent from '#app/components/common/image-component'
import PlentyBody from '#app/components/typography/plenty-body'

import PlentyHeading from '#app/components/typography/plenty-heading.tsx'
import { Button } from '#app/components/ui/button.tsx'
import {
	Carousel,
	CarouselContent,
	CarouselItem,
} from '#app/components/ui/carousel.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import PlentyBodyLink from '#app/components/ui/plenty-body-link.tsx'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '#app/components/ui/select.tsx'
import useAmplitudeContext from '#app/hooks/use-amplitude-context'
import { useTrackPageView } from '#app/hooks/use-track-page-view'
import {
	type action,
	type loader,
} from '#app/routes/_public+/shop-the-look+/$slug.tsx'
import { useCartStore } from '#app/store/cart.ts'
import { type Outfit, type OutfitStyleVariant } from '#types/outfit.ts'
import BagItem from './components/bag-item'
import BreadcrumbShopTheLook from './components/breadcrumb-shop-the-look'
import { getProductPrice, getTotalPrice } from './utils'

interface OutFitProduct {
	variant: ProductVariant
	storeKey?: string
	productId: string
	shippingMethod: string
}

const OutfitPdp = () => {
	const { cart } = useCartStore()
	const sheetTriggerRef = useRef<HTMLButtonElement | null>(null)
	useTrackPageView('shop the look pdp')
	const [isSheetButtonClicked, setIsSheetButtonClicked] = useState(false)
	const { outfit, relatedOutfits } = useLoaderData<typeof loader>()
	let { t } = useTranslation('shop_the_look')
	const { trackAmplitudeEvent } = useAmplitudeContext()
	const location = useLocation()
	const addToBagFetcher = useFetcher<typeof action>({
		key: 'addToBagShopTheLook',
	})
	const [selectedProducts, setSelectedProducts] = useState<{
		[productId: string]: OutFitProduct
	}>({})

	const genderQueryString = `?gender=${encodeURIComponent(
		outfit.gender.toLowerCase(),
	)}`

	useEffect(() => {
		if (isSheetButtonClicked && sheetTriggerRef.current) {
			sheetTriggerRef.current.click()
			setIsSheetButtonClicked(false)
		}
	}, [isSheetButtonClicked])

	useEffect(() => {
		setSelectedProducts({})
	}, [location.pathname])

	const handleAddToBag = () => {
		const productsArray = Object.values(selectedProducts)
		trackAmplitudeEvent('pdp add to bag clicked', {
			outfitName: outfit.name,
			outfitKey: outfit.key,
			productsCount: productsArray.length,
			items: productsArray.map(product => ({
				productKey: product.productId,
				variantSku: product.variant.sku,
			})),
		})
		addToBagFetcher.submit(
			{ products: JSON.stringify(productsArray) },
			{ method: 'post' },
		)

		setSelectedProducts({})
	}

	const handleSelectChange = (productId: string, variant: ProductVariant) => {
		trackAmplitudeEvent('pdp look size selected', {
			outfitName: outfit.name,
			outfitKey: outfit.key,
			productKey: productId,
			variantSku: variant.sku,
		})
		setSelectedProducts(prev => ({
			...prev,
			[productId]: {
				variant: variant,
				storeKey: outfit.storeKey,
				productId: productId,
				shippingMethod: 'delivery',
			},
		}))
	}
	const isAnyVariantInStock = (styleVariant: OutfitStyleVariant) => {
		return styleVariant.variants.some(variant => isSizeAvailable(variant))
	}

	function getFormattedSize(variant: ProductVariant) {
		const size = variant.attributes?.find(item => item.name === 'size')?.value

		const length = variant.attributes?.find(item => item.name === 'length')
			?.value

		return length ? `${size} / ${length.replace('"', '')}` : `${size}`
	}

	const isSizeAvailable = (variant: ProductVariant) => {
		const variantAvailability = getVariantAvailability(variant)
		const variantCartQuantity = getVariantCartQuantity(variant)

		return variantAvailability > variantCartQuantity
	}

	const getVariantAvailability = (variant: ProductVariant) => {
		const availability = Object.values(
			variant.availability?.channels as ProductVariantChannelAvailabilityMap,
		)

		return availability.reduce((total, channelAvailability) => {
			const quantity = channelAvailability.availableQuantity || 0
			return total + quantity
		}, 0)
	}

	const getVariantCartQuantity = (variant: ProductVariant) => {
		if (!cart || !cart.lineItems || cart.lineItems.length === 0) {
			return 0
		}

		return cart.lineItems.reduce((acc: number, item: LineItem) => {
			const isCorrectVariant = item.variant.sku === variant.sku
			if (isCorrectVariant) {
				return acc + item.quantity
			}
			return acc
		}, 0)
	}

	return (
		<div className="flex w-full flex-col px-4 pb-6 pt-2 md:px-[var(--plenty-padding)]">
			<BreadcrumbShopTheLook
				gender={outfit.gender}
				name={outfit.name}
				type={'pdp'}
			/>
			<BagItem />
			<div className="flex flex-col gap-4 md:gap-8">
				<div
					id="product-full-header"
					className="flex h-auto flex-col items-start gap-2 md:gap-4 lg:flex-row"
				>
					<div
						id="photo"
						className="relative isolate flex flex-row items-start gap-4 md:sticky md:top-[120px] lg:min-w-[484px]"
					>
						{outfit.mediaType === 'VIDEO' ? (
							<video
								src={outfit.mediaUrl}
								autoPlay
								muted
								loop
								playsInline
								className="z-0 w-full gap-2 rounded-lg object-cover transition duration-300 ease-in-out lg:max-h-[644px]"
							/>
						) : (
							<img
								src={outfit.mediaUrl}
								alt={outfit.name}
								className="z-0 w-full gap-2 rounded-lg object-cover transition duration-300 ease-in-out lg:max-h-[644px]"
							/>
						)}
					</div>

					<div
						id="content"
						className="flex w-full flex-col items-start gap-4 md:max-w-[484px]"
					>
						<PlentyBody size="xs">
							<strong>{t('shop_the_look')}:</strong>{' '}
							{getTotalPrice(outfit.styleVariants)}
						</PlentyBody>
						{outfit.styleVariants.map((styleVariant: OutfitStyleVariant) => (
							<div
								key={styleVariant.styleNumber + styleVariant.styleKey}
								id="product"
								className="flex w-full flex-row gap-4 md:max-w-[584px]"
							>
								<Link
									className="flex flex-grow"
									to={`/product/${styleVariant.styleNumber}?style=${styleVariant.styleKey}`}
									onClickCapture={() => {
										trackAmplitudeEvent('pdp product clicked', {
											outfitName: outfit.name,
											outfitKey: outfit.key,
											productName: styleVariant.productName,
											productKey: styleVariant.productId,
										})
									}}
								>
									<ImageComponent
										src={styleVariant.variants?.[0]?.images?.[0]?.url ?? ''}
										alt={outfit.name}
										className="object-fit w-[200px] rounded-lg transition duration-300 ease-in-out"
									/>
								</Link>
								<div className="flex w-full flex-col items-start justify-start space-y-4">
									<div className="flex w-full flex-col space-y-1">
										<div className="flex w-full flex-row justify-between">
											<PlentyBody size="sm">
												{
													styleVariant?.variants?.[0]?.attributes?.find(
														(a: any) => a.name === 'brand',
													)?.value
												}
											</PlentyBody>
											<PlentyBody size="sm">
												{getProductPrice(styleVariant.variants)}
											</PlentyBody>
										</div>
										<div>
											<PlentyBody size="sm">
												{styleVariant.productName}
											</PlentyBody>
										</div>
									</div>
									<div className="w-full">
										<Select
											key={styleVariant.productId}
											disabled={!isAnyVariantInStock(styleVariant)}
											value={
												selectedProducts[styleVariant.productId]?.variant.sku ||
												''
											}
											onValueChange={value => {
												const variant = styleVariant.variants.find(
													(v: any) => v.sku === value,
												)
												if (variant) {
													handleSelectChange(styleVariant.productId, variant)
												}
											}}
										>
											<SelectTrigger className="hover:shadow-sm">
												<SelectValue
													placeholder={
														!isAnyVariantInStock(styleVariant)
															? t('out_of_stock')
															: t('select_size')
													}
												/>
											</SelectTrigger>
											<SelectContent className="z-50" id="pdp-select-size">
												{styleVariant.variants.map(
													(variant: ProductVariant) => {
														return (
															isSizeAvailable(variant) && (
																<SelectItem
																	key={variant.sku}
																	value={variant.sku!}
																>
																	{getFormattedSize(variant)}
																</SelectItem>
															)
														)
													},
												)}
											</SelectContent>
										</Select>
									</div>
								</div>
							</div>
						))}
						<div className="flex w-full flex-col space-y-4">
							<Button
								variant={'primary'}
								size={'primary'}
								fullWidth
								className="rounded-lg"
								onClick={() => handleAddToBag()}
								disabled={Object.keys(selectedProducts).length === 0}
							>
								{t('add_to_shopping_bag')}
							</Button>
							<div className="flex flex-col space-y-1">
								{/* Will leave this here since Kelly wants it for v2
								<div className="flex items-center space-x-2">
									<Icon name="store" size="lg" />
									<PlentyBody size="md">
										{t('click_and_collect_in_store', { count: 1 })}.
									</PlentyBody>
									 <ChooseShopTheLookStore
										stores={stores}
										selectedProducts={selectedProducts}
										triggerLabel={t('see_which_ones')}
										sheetTitle={t('click_and_collect_in_store', { count: 1 })}
										triggerClassName="underline"
										triggerOnClick={handleSheetTriggerClick}
										sheetTriggerRef={sheetTriggerRef}
									/>
								</div>*/}

								<div className="flex items-center space-x-2">
									<Icon name="info-circle" size="lg" />
									<PlentyBody size="md">
										{t('click_and_collect_label')}
									</PlentyBody>
								</div>
							</div>
						</div>
					</div>
				</div>
				{relatedOutfits.length > 0 && (
					<div
						id="explore"
						className="flex flex-col space-y-4 py-0 pt-4 md:space-y-8 md:py-4 md:pt-0"
					>
						<div
							id="header"
							className="flex flex-col justify-between space-y-2 md:flex-row md:space-y-0"
						>
							<PlentyHeading as="h7">{t('looks_might_suit_you')}</PlentyHeading>
							<div className="flex flex-col justify-end">
								<Link
									to={`/shop-the-look${genderQueryString}`}
									onClickCapture={() =>
										trackAmplitudeEvent('pdp explore all looks clicked', {
											currentOutfitName: outfit.name,
											currentOutfitKey: outfit.key,
										})
									}
								>
									<div className="z-10 flex">
										<PlentyBodyLink as="nav-sm">{t('explore')}</PlentyBodyLink>
									</div>
									<div className="-mt-2 rounded-lg border-4 border-beige-50" />
								</Link>
							</div>
						</div>
						<div id="content" className="flex flex-col space-y-6">
							<Carousel
								plugins={[WheelGesturesPlugin()]}
								opts={{ dragFree: true }}
							>
								<CarouselContent className="pl-0">
									{relatedOutfits.map((look: Outfit, index: any) => (
										<CarouselItem
											key={index}
											className="flex basis-auto flex-col items-start space-y-4"
										>
											<Link
												to={`/shop-the-look/${look.key}`}
												onClickCapture={() =>
													trackAmplitudeEvent('pdp carousel look clicked', {
														outfitName: look.name,
														outfitKey: look.key,
													})
												}
											>
												{look.mediaType === 'VIDEO' ? (
													<video
														src={look.mediaUrl}
														autoPlay
														muted
														loop
														playsInline
														className="h-[220px] w-[168px] rounded-lg object-cover transition duration-300 ease-in-out md:h-[416px] md:w-[284px]"
													/>
												) : (
													<img
														src={look.mediaUrl}
														alt={look.name}
														className="h-[220px] w-[168px] rounded-lg object-cover transition duration-300 ease-in-out md:h-[416px] md:w-[284px]"
													/>
												)}
											</Link>
											<div
												id="social-selling"
												className="flex flex-col md:w-full md:flex-row md:justify-between"
											>
												<PlentyBody size="md" className="text-beige-100">
													{look.name}
												</PlentyBody>
												<PlentyBody size="md">
													{getTotalPrice(look.styleVariants)}
												</PlentyBody>
											</div>
										</CarouselItem>
									))}
								</CarouselContent>
							</Carousel>
						</div>
					</div>
				)}
			</div>
		</div>
	)
}

export default OutfitPdp
